// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import type { DeepReadonly } from '@evolvemep/api-ts';
import { Button, Checkbox, Group, LoadingOverlay, Stack, Text } from '@mantine/core';

import type { APSViewerCommands } from 'components/Autodesk/extensions/APSViewerStoreExtension/createAPSViewerStore';
import { sortPropertiesComp } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/helpers';
import type { SelectedElementProperty } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/types';
import type { ForgeViewerSelection } from 'components/Autodesk/extensions/SelectionToolsExtension/types';
import {
  useAutodeskExtensionStoreActions,
  useAutodeskExtensionStoreSnapshot,
} from 'components/Autodesk/useAutodeskExtensionStore';
import { TextInputDebounced } from 'components/Mantine/TextInputDebounced';
import { convertInchesToImperialString } from 'helpers/convertInchesToImperialString';
import { isNil, isNotNil } from 'helpers/isNotNil';

export const hasBlankValue = (property?: Autodesk.Viewing.Property | null): boolean =>
  isNil(property) || String(property.displayValue).trim() === '';

/**
 * Formats a property value for display in the panel. If the property has no value, returns an empty string.
 */
export const formatProperty = (property: Pick<SelectedElementProperty, 'value'>, blankText = '') => {
  if (isNil(property.value) || String(property.value.displayValue).trim() === '') {
    return blankText;
  }
  // TODO: Narrow these down and verify the values.
  if (property.value.units?.startsWith('autodesk.unit.unit:inches')) {
    return convertInchesToImperialString(Number(property.value.displayValue), { tolerance: 1 / 32 });
  }
  if (property.value.units?.startsWith('autodesk.unit.unit:feet')) {
    return convertInchesToImperialString(Number(property.value.displayValue) * 12, { tolerance: 1 / 16 });
  }
  return String(property.value.displayValue);
};

/** Gets the properties available on the currently selected element(s) for the panel's picker. */
const getSelectedProperties = async (
  selection: DeepReadonly<ForgeViewerSelection[]>,
  commands: APSViewerCommands,
): Promise<SelectedElementProperty[]> => {
  const propertiesWithDbId = (await commands.getBulkProperties(selection.map((s) => s.dbId))).flatMap(
    ({ properties, dbId }) => properties.map((p) => ({ ...p, dbId })),
  );

  const names = [...new Set(propertiesWithDbId.map((p) => p.displayName))].toSorted(sortPropertiesComp);

  return names.map((name) => {
    const matches = propertiesWithDbId.filter((p) => p.displayName === name);
    const value = selection.length === 1 ? (matches[0] ?? null) : null;
    const dbIdsWithValue = matches.filter((p) => !hasBlankValue(p)).map((p) => p.dbId);
    const dbIdsWithProperty = matches.map((p) => p.dbId);
    return { name, value, dbIdsWithValue, dbIdsWithProperty };
  });
};

/** Renders the list of properties for the selected element or elements. */
export const ElementTagProperties = () => {
  const [search, setSearch] = useState('');

  const { selection, commands: viewerCommands } = useAutodeskExtensionStoreSnapshot('apsViewer');
  const { draftTags, persistence, isSaving } = useAutodeskExtensionStoreSnapshot('elementTags');
  const { addFromSelection, removeFromSelection, removeAllFromSelection, setTagsVisible } =
    useAutodeskExtensionStoreActions('elementTags');

  const [properties, setProperties] = useState<SelectedElementProperty[] | null>(null);
  const propertiesLoading = properties === null;

  useEffect(() => {
    setProperties(null);
    if (isNil(viewerCommands) || selection.length === 0) return undefined;
    let cancelled = false;

    void getSelectedProperties(selection, viewerCommands).then((selectedProperties) => {
      if (cancelled) return;
      setProperties(selectedProperties);
    });
    return () => {
      cancelled = true;
    };
  }, [viewerCommands, selection]);

  const canChange = (isNotNil(persistence) && !isSaving) || propertiesLoading;
  const onAdd = (selectedProperty: SelectedElementProperty) => {
    if (!canChange) return;
    setTagsVisible(true);
    // Some of our selected elements may not have the property, so we filter the selection to only those that do.
    const selectionWithProperty = selection.filter((s) => selectedProperty.dbIdsWithProperty.includes(s.dbId));
    addFromSelection(selectedProperty.name, selectionWithProperty);
  };

  const onRemove = (property: SelectedElementProperty['name']) => {
    if (!canChange) return;
    setTagsVisible(true);
    removeFromSelection(property, selection);
  };

  const onDeselectAllProperties = () => {
    if (!canChange) return;
    removeAllFromSelection(selection);
  };

  // Filters the list of draft tags to only those that are currently selected in the viewer.
  const selectedTags = useMemo(() => {
    const selectionExternalIds = new Set(selection.map((s) => s.externalId));
    return (draftTags ?? []).filter((tag) => selectionExternalIds.has(tag.elementId));
  }, [selection, draftTags]);

  // Generates a map of property names to the number of selected tags that have that property.
  const tagPropertiesMap = useMemo(
    () =>
      selectedTags.reduce((acc, tag) => {
        for (const property of tag.properties) {
          acc.set(property, (acc.get(property) ?? 0) + 1);
        }
        return acc;
      }, new Map<SelectedElementProperty['name'], number>()),
    [selectedTags],
  );

  // Returns the list of properties that match the current search, or all properties if the search is empty.
  const searchProperties = useMemo(
    () => properties?.filter((property) => property.name.toLowerCase().includes(search.toLowerCase())),
    [properties, search],
  );

  return (
    <Stack>
      <TextInputDebounced onChange={(value) => setSearch(value)} placeholder="Search" />
      <Stack pos="relative" h={200} style={{ overflowY: 'auto' }}>
        <LoadingOverlay visible={propertiesLoading} />
        {searchProperties?.map((property) => {
          // Values we'll need to calculate if the box is checked or indeterminate.
          const hasPropertyCount = property.dbIdsWithProperty.length;
          const tagCountWithProperty = tagPropertiesMap.get(property.name);
          const hasTagsWithProperty = isNotNil(tagCountWithProperty);

          // If the property has tags, but not for all the selected elements with the property, it's indeterminate.
          const indeterminate = hasTagsWithProperty && tagCountWithProperty < hasPropertyCount;

          const valueCount = property.dbIdsWithValue.length;
          const displayValue =
            selection.length === 1 ? formatProperty(property) : valueCount > 0 ? `(${valueCount})` : '';
          return (
            <Group key={property.name} gap="md" justify="space-between" wrap="nowrap">
              <Checkbox
                label={property.name}
                checked={hasTagsWithProperty}
                onChange={() => {
                  if (hasTagsWithProperty) {
                    onRemove(property.name);
                  } else {
                    onAdd(property);
                  }
                }}
                indeterminate={indeterminate}
                disabled={!canChange}
              />
              <Text
                size="sm"
                c="dimmed"
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 1 }}
              >
                {displayValue}
              </Text>
            </Group>
          );
        })}
        {searchProperties?.length === 0 && (
          <Text c="dimmed">
            {properties?.length === 0
              ? 'No properties found for the selected element(s).'
              : 'No properties match your search.'}
          </Text>
        )}
      </Stack>
      {tagPropertiesMap.size > 0 && (
        <Group justify="flex-start">
          <Button variant="subtle" size="compact-sm" px={0} disabled={!canChange} onClick={onDeselectAllProperties}>
            Deselect all properties ({tagPropertiesMap.size})
          </Button>
        </Group>
      )}
    </Stack>
  );
};
